import * as React from 'react';

import { HeroModule, isInvertedColor, toLayoutValue } from '@coursera-web/cds-labs';

import { CheckIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import EnrollOrUpgradeToCourseraPlus from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/EnrollOrUpgradeToCourseraPlus';
import type { PromoTemplateHeroFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import Markdown from 'bundles/promo-template/contentful/core/Markdown';
import useEnrollOrUpgradeToCPlusButton from 'bundles/promo-template/hooks/useEnrollOrUpgradeToCPlusButton';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;
type HeroModuleSecondaryCta = HeroModuleProps['secondaryCta'];

export type Props = {
  content: PromoTemplateHeroFragment & {
    preloadImage?: boolean;
    lazyLoadImage?: boolean;
    removeTopPadding?: boolean;
  };
  slug?: string;
};

export const mapContentfulObjects = (
  content: Pick<
    PromoTemplateHeroFragment,
    | 'ctaTarget'
    | 'ctaText'
    | 'secondaryCtaTarget'
    | 'secondaryCtaText'
    | 'secondaryCtaVariant'
    | 'eyebrow'
    | 'eyebrowBackground'
    | 'eyebrowImage'
  >
) => {
  let cta: HeroModuleProps['cta'] = null;
  if (content?.ctaTarget && content?.ctaText) {
    cta = {
      target: content.ctaTarget,
      label: content.ctaText,
    };
  }

  let secondaryCta: HeroModuleSecondaryCta = null;
  if (content?.secondaryCtaTarget && content?.secondaryCtaText) {
    secondaryCta = {
      target: content.secondaryCtaTarget,
      label: content.secondaryCtaText,
      // Contentful -> Expected type conversion
      variant: content.secondaryCtaVariant as 'ghost' | 'secondary',
    };
  }

  let eyebrow: HeroModuleProps['eyebrow'];
  if (content?.eyebrow || content?.eyebrowImage) {
    eyebrow = {
      label: content?.eyebrow,
      image: content?.eyebrowImage,
      backgroundColor: content?.eyebrowBackground,
    };
  }

  return {
    cta,
    secondaryCta,
    eyebrow,
  };
};

export function HeroModuleRenderer({ content, slug }: Props) {
  const track = useTracker();

  const trackClick = (trackingName: ButtonName, linkTarget?: string) => () => {
    track('click_button', {
      button: { name: trackingName, linkURL: linkTarget ?? '' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const isDarkBackground = isInvertedColor(content?.backgroundColor ?? '');

  const customPrimaryCta = useEnrollOrUpgradeToCPlusButton({
    button: (
      <EnrollOrUpgradeToCourseraPlus
        ctaLabel={content?.ctaText}
        ctaVariant={isDarkBackground ? 'secondary' : 'primary'}
        trackingName="hero_primary_cta"
        trackEnrollCtaClick={trackClick('hero_primary_cta', content?.ctaTarget as string)}
      />
    ),
    ctaTarget: content?.ctaTarget,
    slug,
  });

  if (!content) return null;

  const ListItem = ({ children }: { children: React.ReactNode }) => {
    return (
      <li>
        <CheckIcon size="small" color="interactive" />
        <div>{children}</div>
      </li>
    );
  };

  const description = <Markdown source={content.description} module={content} renderers={{ listItem: ListItem }} />;

  return (
    <HeroModule
      {...content}
      layout={toLayoutValue(content.layout)}
      description={description}
      {...mapContentfulObjects(content)}
      customPrimaryCta={customPrimaryCta}
    />
  );
}

export default HeroModuleRenderer;
